/**
 * Hook that resolves org members to display info keyed by `user_id`.
 *
 * Uses the SECURITY DEFINER RPC `get_org_members_with_emails(p_org_id)` so
 * authenticated clients can render real names/emails without touching
 * `auth.users` directly. The returned map is designed for cheap lookups
 * in list UIs (e.g. "Created by {name}" on campaign cards).
 *
 * @module lib/useOrgMembersIndex
 */

import { useEffect, useState } from 'react';
import { useSupabase } from './SupabaseProvider';
import { captureException } from './telemetry/posthog/frontend';

/** Display info for a single org member. */
export interface OrgMemberDisplay {
  user_id: string;
  /** Trimmed full name, or null when the user hasn't set one. */
  name: string | null;
  email: string;
}

/** Map of `user_id` → display info for every member of an org. */
export type OrgMembersIndex = Record<string, OrgMemberDisplay>;

/**
 * Fetch members of the given org and index them by `user_id` for display.
 *
 * Returns an empty object until the fetch completes (or when `orgId` is
 * falsy). Errors are captured to PostHog but never thrown - callers fall
 * back to whatever default label they want (e.g. the raw `created_by` id
 * or "Unknown").
 */
export function useOrgMembersIndex(orgId: string | null | undefined): OrgMembersIndex {
  const { supabase } = useSupabase();
  const [index, setIndex] = useState<OrgMembersIndex>({});

  useEffect(() => {
    if (!orgId) {
      setIndex({});
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      try {
        const { data, error } = await supabase.rpc('get_org_members_with_emails', {
          p_org_id: orgId,
        });
        if (error) throw new Error(error.message);
        if (cancelled) return;

        const rows = (data ?? []) as Array<{
          user_id: string;
          full_name: string | null;
          email: string | null;
        }>;
        const map: OrgMembersIndex = {};
        for (const row of rows) {
          const trimmed = (row.full_name ?? '').trim();
          map[row.user_id] = {
            user_id: row.user_id,
            name: trimmed || null,
            email: row.email ?? '',
          };
        }
        setIndex(map);
      } catch (err) {
        if (cancelled) return;
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'useOrgMembersIndex.fetch',
          { orgId },
        );
        setIndex({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, supabase]);

  return index;
}
